/**
 * DeliverPiecesModal — confirm customer handoff for pending deliverable pieces.
 * Same ConfirmationModal pattern as CloseConfirmationModal / ConfirmSaleConfirmationModal.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography, Stack, Divider } from '@mui/material';
import { LocalShipping as LocalShippingIcon } from '@mui/icons-material';
import { ConfirmationModal } from '../../../ui/Modal';
import type { SalesOrder, SaleFulfillmentDetail } from '../../../../types/sales.types';
import {
  getDeliverableUndeliveredItems,
  getItemDisplayName,
} from '../../../../utils/sales/order-delivery.utils';

export interface DeliverPiecesModalProps {
  open: boolean;
  order: SalesOrder | null;
  fulfillmentDetail?: SaleFulfillmentDetail | null;
  onClose: () => void;
  onConfirm: (items: Array<{ itemId: number; isDelivered: boolean }>) => void;
  loading?: boolean;
}

const DeliverPiecesModal: React.FC<DeliverPiecesModalProps> = ({
  open,
  order,
  fulfillmentDetail = null,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const deliverable = useMemo(
    () => (order ? getDeliverableUndeliveredItems(order, fulfillmentDetail) : []),
    [order, fulfillmentDetail],
  );

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    // Default: all deliverable pieces ON (typical handoff).
    setSelectedIds(new Set(deliverable.map((item) => item.id)));
  }, [open, deliverable]);

  const toggle = (itemId: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selectedIds.size === 0) return;
    onConfirm(
      Array.from(selectedIds).map((itemId) => ({
        itemId,
        isDelivered: true,
      })),
    );
  };

  return (
    <ConfirmationModal
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="¿Entregar piezas?"
      message="Se marcarán como entregadas las piezas seleccionadas que están disponibles en sucursal."
      confirmText="Confirmar"
      cancelText="Volver"
      variant="default"
      loading={loading}
      disabled={selectedIds.size === 0}
      icon={<LocalShippingIcon />}
      customContent={
        deliverable.length > 0 ? (
          <Box sx={{ width: '100%', mt: 1, textAlign: 'left' }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Selecciona las piezas a entregar ({selectedIds.size} de {deliverable.length})
            </Typography>
            <Stack spacing={0.25}>
              {deliverable.map((item) => (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(item.id)}
                      onChange={(_, checked) => toggle(item.id, checked)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{getItemDisplayName(item)}</Typography>
                      {item.warehouseInventoryProduct?.productCodeUnique && (
                        <Typography variant="caption" color="text.secondary">
                          {item.warehouseInventoryProduct.productCodeUnique}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', ml: 0 }}
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No hay piezas pendientes disponibles para entregar.
          </Typography>
        )
      }
    />
  );
};

export default DeliverPiecesModal;
